import { useEffect, useState } from 'react';
import { isIsoDate } from '@fd/finance-core';

// Minimal hash router: works on any static host (Cloudflare Pages) without server rewrites.
// Routes are a closed set, so navigation is type-checked. See DECISIONS D-021.

export type Route =
  | { page: 'dashboard' }
  | { page: 'accounts'; bankConnection?: string; bankError?: string; bankPerson?: string; bankConnect?: string }
  | { page: 'transactions'; id?: string; categoryId?: string; accountId?: string; date?: string; view?: string; kind?: 'income' | 'expenses' | 'transfers' }
  | { page: 'recurring' }
  | { page: 'budgets' }
  | { page: 'loans' }
  | { page: 'loan'; id?: string }
  | { page: 'people' }
  | { page: 'settlements' }
  | { page: 'savings' }
  | { page: 'netWorth' }
  | { page: 'simulation' }
  | { page: 'settings'; section?: string };

export type Page = Route['page'];

const PAGES: readonly Page[] = ['dashboard', 'accounts', 'transactions', 'recurring', 'budgets', 'loans', 'loan', 'people', 'settlements', 'savings', 'netWorth', 'simulation', 'settings'];

export function parseHash(hash: string): Route {
  const [path = '', query = ''] = hash.replace(/^#\/?/, '').split('?');
  const page = (PAGES as readonly string[]).includes(path) ? (path as Page) : 'dashboard';
  const params = Object.fromEntries(new URLSearchParams(query));
  delete params.page;
  if (params.date && !isIsoDate(params.date)) delete params.date;
  if (params.kind && !['income', 'expenses', 'transfers'].includes(params.kind)) delete params.kind;
  return { ...params, page } as Route;
}

export function hrefFor(route: Route): string {
  const { page, ...params } = route;
  const query = new URLSearchParams(
    Object.entries(params).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
  ).toString();
  return `#/${page}${query ? `?${query}` : ''}`;
}

export function navigate(route: Route): void {
  location.hash = hrefFor(route);
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseHash(location.hash));
  useEffect(() => {
    const listener = () => setRoute(parseHash(location.hash));
    addEventListener('hashchange', listener);
    return () => removeEventListener('hashchange', listener);
  }, []);
  return route;
}
